/**
 * The drawing tools, as the lower half of the chart's vertical strip.
 *
 * Thirty-three tools behind six buttons. The strip is 34px wide, so they are
 * **grouped** — one button per group, and picking a group pops its members out
 * sideways, which is the shape of a drawing palette in any charting app and the
 * reason the strip does not simply grow thirty more slots.
 *
 * ## Two sources, one menu
 *
 * `lib/trading-tools.ts` mixes them deliberately:
 *
 * - **builtin** — a contract `Annotation`, placed by our own `DrawingModifier`.
 * - **trading** — a `scichart-financial-tools` annotation, placed by the
 *   library's own modifier and then adopted by the builder.
 *
 * **Both are shredded into `Pane.annotations` by qv-data and are still there
 * after a reload**, so the discriminator now decides only which modifier arms.
 * It used to decide what survived — the contract had seven kinds where this
 * palette has twenty-six — and the tooltips said so.
 *
 * Grouped by what you are drawing rather than by which library draws it. That
 * difference is ours, not the user's — Trend line sits beside Extended line
 * because that is where someone looks for it.
 *
 * ## Arming is modal, and only one thing can be armed
 *
 * Two modifiers are listening and only one may be live, so arming either kind
 * disarms the other. Clicking the armed tool disarms it: a modal cursor with no
 * visible way out is the thing people hate about drawing tools, so the same
 * click both arms and cancels.
 */

import { useEffect, useRef, useState } from "react";
import type { Annotation } from "@qufolio/model";

import { TRADING_TOOL_GROUPS, type TradingTool } from "../lib/trading-tools";

/**
 * What the pointer is armed with.
 *
 * The contract kinds, plus `"plan"` — the trade-plan gesture, which is captured
 * by the same modifier and produces a journal row rather than an annotation.
 * One slot because there is one pointer.
 */
export type AnnotationTool = Annotation["type"] | "plan";

export interface AnnotationToolsProps {
  /** The armed contract tool, or `null`. Owned by the caller — see `onArm`. */
  active: AnnotationTool | null;
  /**
   * Arm a contract tool, or `null` to disarm.
   *
   * Lifted rather than held here because the *chart* needs to know what is
   * armed — the cursor changes and the next drag is captured — and a component
   * that owned it privately would have to publish it anyway.
   */
  onArm: (tool: AnnotationTool | null) => void;
  /** The armed financial-tools annotation type, or `null`. */
  activeTrading?: string | null;
  onArmTrading?: (type: string | null) => void;
  /** Remove every drawing from the active chart — both sources. */
  onClear?: () => void;
  /** Which edge the strip is attached to, so a popout opens away from it. */
  side: "left" | "right";
}

export function AnnotationTools({
  active,
  onArm,
  activeTrading,
  onArmTrading,
  onClear,
  side,
}: AnnotationToolsProps) {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  /**
   * The last tool picked in each group, so the face remembers it.
   *
   * Without this the face is always the group's first tool, and someone who
   * draws pitchforks would go through the list every single time — which is the
   * two clicks this split button exists to remove.
   */
  const [lastPicked, setLastPicked] = useState<Record<string, TradingTool>>({});
  const hostRef = useRef<HTMLDivElement>(null);

  // A popout is transient: anything else the user does dismisses it. Pointer
  // rather than click, so it closes on the press that starts a drag on the
  // canvas rather than after the drag finishes.
  useEffect(() => {
    if (openGroup === null) return;
    const dismiss = (event: PointerEvent) => {
      if (!hostRef.current?.contains(event.target as Node)) setOpenGroup(null);
    };
    window.addEventListener("pointerdown", dismiss);
    return () => window.removeEventListener("pointerdown", dismiss);
  }, [openGroup]);

  const isArmed = (tool: TradingTool): boolean => {
    if (tool.kind === "plan") return active === "plan";
    if (tool.kind === "builtin") return active === tool.tool;
    return activeTrading === tool.type;
  };

  /** One click, whichever source the tool comes from. */
  const arm = (tool: TradingTool) => {
    const already = isArmed(tool);
    const group = TRADING_TOOL_GROUPS.find((g) => g.tools.includes(tool));
    if (group) setLastPicked((was) => ({ ...was, [group.name]: tool }));
    if (tool.kind === "plan") {
      // Rides the contract-tool slot: the gesture is captured by the same
      // modifier, and only one thing may be armed either way.
      onArm(already ? null : "plan");
      onArmTrading?.(null);
    } else if (tool.kind === "builtin") {
      onArm(already ? null : (tool.tool as AnnotationTool));
      onArmTrading?.(null);
    } else {
      onArmTrading?.(already ? null : tool.type);
      onArm(null);
    }
    setOpenGroup(null);
  };

  return (
    <div ref={hostRef} style={{ display: "contents" }}>
      {TRADING_TOOL_GROUPS.map((group) => {
        const armedTool = group.tools.find(isArmed);
        // What the face draws with. The armed one when it is this group's, else
        // whatever was last picked here, else the first — so a group you have
        // used remembers your choice and one you have not starts sensibly.
        const current = armedTool ?? lastPicked[group.name] ?? group.tools[0]!;
        return (
          <div key={group.name} style={{ position: "relative" }}>
            <button
              type="button"
              // The face **arms**, it does not open a menu. Opening one was a
              // click spent to reach the click that draws, on every drawing.
              title={`${current.label} — click to draw, ${CHEVRON[side]} for ${group.name}`}
              aria-label={current.label}
              onClick={() => arm(current)}
              // The glyph centres in what the edge tab leaves, not under it.
              style={{
                ...SLOT,
                ...(side === "right" ? { paddingLeft: TAB_WIDTH } : { paddingRight: TAB_WIDTH }),
                ...(armedTool ? ARMED : null),
              }}
            >
              {current.glyph}
            </button>

            {/* The list, behind its own target. A corner rather than a second
                slot beside it: the strip is 34px and two 30px buttons do not
                fit, and a split button is the shape this already is — a default
                action with the alternatives one step away.

                It points **the way the popout opens**, which is toward the
                charts: the strip hangs on the chart region's edge, so with the
                chart to the left it opens left and reads `‹`. */}
            <button
              type="button"
              title={`${group.name} — ${group.tools.map((t) => t.label).join(", ")}`}
              aria-label={`Choose from ${group.name}`}
              aria-expanded={openGroup === group.name}
              aria-haspopup="menu"
              onClick={(event) => {
                event.stopPropagation();
                setOpenGroup((open) => (open === group.name ? null : group.name));
              }}
              style={{
                ...CHEVRON_SLOT,
                ...(side === "right"
                  ? { left: 0, borderRight: "1px solid var(--qf-border)", borderRadius: "4px 0 0 4px" }
                  : { right: 0, borderLeft: "1px solid var(--qf-border)", borderRadius: "0 4px 4px 0" }),
                ...(openGroup === group.name
                  ? { color: "var(--qf-accent)", background: "var(--qf-surface)" }
                  : null),
              }}
            >
              {CHEVRON[side]}
            </button>

            {openGroup === group.name && (
              <div
                role="menu"
                aria-label={group.name}
                // Opens away from the edge the strip is on, so it never renders
                // off-screen — the strip docks left or right depending on where
                // the screener is.
                style={{ ...POPOUT, [side === "left" ? "left" : "right"]: 38 }}
              >
                {group.tools.map((tool) => (
                  <button
                    key={
                      tool.kind === "plan"
                        ? "plan"
                        : tool.kind === "builtin"
                          ? `b:${tool.tool}`
                          : `t:${tool.type}`
                    }
                    type="button"
                    role="menuitem"
                    // No longer marks which ones persist: they all do. The
                    // label alone is the honest tooltip now, and a note saying
                    // "saved with the workspace" on half the menu would read as
                    // a warning about the other half.
                    title={
                      tool.kind === "plan"
                        ? "Trade plan — drag from the entry to the stop; it lands in the journal"
                        : tool.label
                    }
                    onClick={() => arm(tool)}
                    style={{ ...ITEM, ...(isArmed(tool) ? ITEM_ARMED : null) }}
                  >
                    {/* Fixed-width, so the names line up into a column rather
                        than stepping in and out with each glyph's width. */}
                    <span style={ITEM_GLYPH} aria-hidden>
                      {tool.glyph}
                    </span>
                    {tool.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {onClear && (
        <button
          type="button"
          title="Remove every drawing from this chart (the plan's own lines stay)"
          aria-label="Clear drawings"
          onClick={onClear}
          // **Pushed to the bottom of the half**, not left sitting under the
          // last tool. `margin-top: auto` in a flex column takes all the free
          // space above it, which puts a destructive button as far from the ones
          // you reach for constantly as the strip allows — and gives it a fixed
          // home rather than one that moves every time a tool is added.
          style={{ ...SLOT, marginTop: "auto" }}
        >
          ⌫
        </button>
      )}
    </div>
  );
}

const SLOT: React.CSSProperties = {
  // 34 rather than the 26 this started at. These are text glyphs, not drawn
  // icons, so they carry less weight per pixel than an SVG in the same box — a
  // pitchfork at 14px in a 26px slot read as a smudge. 34 is also the strip's
  // own width, so a key now fills it edge to edge.
  width: 34,
  height: 34,
  margin: "1px 0",
  padding: 0,
  display: "grid",
  placeItems: "center",
  // The app background, not transparent: the strip sits on `--qf-surface-alt`,
  // so a transparent button has no edge at all. Against the background each
  // slot reads as a key, which is what it is.
  background: "var(--qf-background)",
  border: "1px solid transparent",
  borderRadius: 4,
  // A token, never `#fff`: hardcoded white left every drawing tool invisible
  // on the White theme, a white glyph on a white key (D13).
  color: "var(--qf-text)",
  cursor: "pointer",
  font: "inherit",
  // 21 in a 34 key, up from 18 — the same "fill the key" pass the SVG half of
  // the strip got, and kept two above the SVGs' 19 because a font glyph does
  // not fill its em box the way a drawing fills its viewBox. The key itself is
  // unchanged.
  fontSize: 21,
  // **No `-webkit-text-stroke` here.** It was the only thing that thickened
  // these — `╱ ▭ ⫽ ≡` are box-drawing and mathematical characters served by a
  // fallback face that ships one weight, so `fontWeight` alone returns the same
  // outline — and painting a 0.4px outline over them was too heavy at this size:
  // the glyphs are line drawings and the strokes started closing the shapes up.
  // The weight below stays for the glyphs whose face *does* have a bold cut.
  fontWeight: 600,
  lineHeight: 1,
};

/**
 * The armed tool is lit, because a modal cursor must say so.
 *
 * The border carries it rather than the fill: every slot now has the background
 * colour, so a filled highlight would fight it, where an accent outline reads
 * instantly against a row of identical keys.
 */
const ARMED: React.CSSProperties = {
  // **`selection`, because `--qf-surface-raised` does not exist.** The theme
  // publishes fourteen tokens and that is not among them, so this resolved to
  // nothing and the armed key had no fill — the accent border was carrying the
  // whole "this tool is live" signal alone. `selection` is what
  // `GridToolbarButton` already lights an active key with, so the two halves of
  // the strip now agree.
  background: "var(--qf-selection)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-text)",
};

/**
 * Which way the list opens, as a glyph.
 *
 * The strip hangs on an edge of the *chart region* and the popout opens toward
 * the charts, never off-screen. So with the strip on the region's right edge —
 * the chart to its left — the list comes out leftwards and the chevron says so.
 */
const CHEVRON: Record<"left" | "right", string> = { left: "›", right: "‹" };

/** The edge tab's width, which the face gives up so its glyph stays centred. */
const TAB_WIDTH = 10;

/**
 * The chevron's target: a **tab down the face's edge** (owner, 2026-10-07).
 *
 * It was an 11px corner in muted grey, and nobody saw it — "this side menu is
 * not that visible". Now it runs the full height of the key with its own rule,
 * the chevron centred in it, so "there is more here" reads at a glance and the
 * target is three times the size. Still inside the 34px key: the strip has no
 * room for a second slot beside it. On the side the list opens from, so it
 * points out of the edge the menu appears at; accent while its list is open.
 */
const CHEVRON_SLOT: React.CSSProperties = {
  position: "absolute",
  top: 2,
  bottom: 2,
  width: TAB_WIDTH,
  padding: 0,
  display: "grid",
  placeItems: "center",
  background: "var(--qf-surface-alt)",
  border: "none",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  font: "inherit",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1,
};

/**
 * The list, as icon-then-name rows.
 *
 * A column of labelled rows rather than a row of bare glyphs. Eight unlabelled
 * icons is a rebus — `⋔` against `⋎` against `⋏` are three pitchforks and the
 * difference between them is not in the glyph. The name is the thing being
 * chosen; the icon is how you find it again afterwards on the face.
 */
const POPOUT: React.CSSProperties = {
  position: "absolute",
  top: 0,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  padding: 3,
  minWidth: 168,
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
  zIndex: 30,
};

const ITEM: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "4px 8px",
  background: "transparent",
  border: "1px solid transparent",
  borderRadius: 4,
  color: "var(--qf-text)",
  cursor: "pointer",
  font: "inherit",
  fontSize: 13,
  lineHeight: 1.3,
  textAlign: "left",
  whiteSpace: "nowrap",
};

const ITEM_ARMED: React.CSSProperties = {
  background: "var(--qf-selection)",
  borderColor: "var(--qf-accent)",
};

/** Fixed width so the names align into a column. */
const ITEM_GLYPH: React.CSSProperties = {
  width: 20,
  textAlign: "center",
  fontSize: 17,
  flex: "0 0 auto",
};
